import React from "react";
import LegalPage, { Section } from "@/components/LegalPage";
import { business, fullAddress } from "@/lib/business";

export default function Privacy() {
  return (
    <LegalPage
      title="Privacy Policy"
      subtitle={`How ${business.name} collects, uses and protects your personal information.`}
      testId="privacy-page"
    >
      <p>
        This Privacy Policy explains how {business.legalName} ("we", "us", "our") collects, uses,
        discloses and safeguards your personal information when you visit {business.website} or make a
        purchase. By using the Site you agree to the practices described here.
      </p>

      <Section n="1" title="Information we collect">
        <p>We collect the following categories of information:</p>
        <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
          <li>
            <span className="text-white font-medium">Contact & account information</span> — name, email,
            password (hashed), phone number, shipping and billing address.
          </li>
          <li>
            <span className="text-white font-medium">Order & payment information</span> — items purchased,
            order value, coupon codes, and a payment-processor token. We <em>never</em> store your full
            card number on our servers; Stripe and PayPal handle that directly.
          </li>
          <li>
            <span className="text-white font-medium">Behavioral & device information</span> — pages
            viewed, products viewed, cart contents, IP address, browser type, device identifiers, and
            cookies/local-storage tokens used to keep you signed in.
          </li>
          <li>
            <span className="text-white font-medium">Marketing preferences</span> — if you subscribe to
            emails (e.g. via our exit-intent WELCOME10 offer), we record your consent.
          </li>
          <li>
            <span className="text-white font-medium">User-generated content</span> — reviews, questions,
            photos and profile info you choose to share on the Site.
          </li>
        </ul>
      </Section>

      <Section n="2" title="How we use your information">
        <p>We use the information to:</p>
        <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
          <li>Process orders, take payment, and deliver products</li>
          <li>Send transactional emails (order confirmation, shipping, returns)</li>
          <li>Send marketing emails you have opted into (you can unsubscribe any time)</li>
          <li>Provide customer support and respond to questions</li>
          <li>Personalize your experience (product recommendations, recently-viewed strip, loyalty tiers)</li>
          <li>Detect fraud and enforce our Terms of Service</li>
          <li>Comply with legal obligations (tax, accounting, law-enforcement requests)</li>
        </ul>
      </Section>

      <Section n="3" title="How we share your information">
        <p>We do <strong>not sell</strong> your personal information. We share it only with:</p>
        <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
          <li>
            <span className="text-white font-medium">Payment processors</span> — Stripe and PayPal, to
            complete transactions. Their privacy policies apply to those transactions.
          </li>
          <li>
            <span className="text-white font-medium">Shipping carriers</span> — USPS, FedEx, UPS, DHL,
            or the fulfillment partner attached to your order, so they can deliver it.
          </li>
          <li>
            <span className="text-white font-medium">Email delivery</span> — Resend, for transactional
            and marketing emails.
          </li>
          <li>
            <span className="text-white font-medium">Analytics & advertising partners</span> — Meta,
            Google, TikTok, Snapchat (only when their pixels are enabled) to measure ad performance.
            These platforms may receive hashed identifiers and event data such as page views and
            purchase totals.
          </li>
          <li>
            <span className="text-white font-medium">Legal authorities</span> — when required by law,
            subpoena or court order.
          </li>
        </ul>
      </Section>

      <Section n="4" title="Cookies & tracking">
        <p>
          We use first-party cookies and localStorage tokens to keep you signed in, remember your cart
          and wishlist, and power the recently-viewed strip. We may also use third-party pixels (Meta,
          Google, TikTok) to measure the effectiveness of our ads. You can disable cookies in your
          browser settings, but parts of the Site may not function properly.
        </p>
      </Section>

      <Section n="5" title="Your rights (GDPR / CCPA / etc.)">
        <p>Depending on where you live, you may have the right to:</p>
        <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
          <li>Access the personal information we hold about you</li>
          <li>Correct or update inaccurate information</li>
          <li>Delete your account and personal data (subject to legal record-keeping obligations)</li>
          <li>Object to or restrict certain processing</li>
          <li>Withdraw consent to marketing emails at any time</li>
          <li>Port your data to another service</li>
          <li>Lodge a complaint with your local data-protection authority</li>
        </ul>
        <p>
          To exercise any of these rights, email{" "}
          <a href={`mailto:${business.supportEmail}`} className="text-primary underline">
            {business.supportEmail}
          </a>{" "}
          with the subject line "Privacy request". We will respond within 30 days.
        </p>
      </Section>

      <Section n="6" title="Data retention">
        <p>
          We keep your account and order information as long as your account is active and for a
          reasonable period afterward for accounting, tax and warranty purposes (typically up to 7
          years). Marketing subscribers are retained until they unsubscribe.
        </p>
      </Section>

      <Section n="7" title="Data security">
        <p>
          We use industry-standard measures — HTTPS/TLS everywhere, bcrypt password hashing, encrypted
          database connections, JWT session tokens — to protect your information. However, no method of
          transmission over the internet is 100% secure, and we cannot guarantee absolute security.
        </p>
      </Section>

      <Section n="8" title="Children's privacy">
        <p>
          The Site is not directed at children under 13, and we do not knowingly collect personal
          information from anyone under 13. If you believe a child has provided us with their
          information, contact us and we will delete it.
        </p>
      </Section>

      <Section n="9" title="International transfers">
        <p>
          If you are located outside {business.address.country}, please note that your information may
          be transferred to and processed in {business.address.country}, where data-protection laws may
          be different. By using the Site you consent to that transfer.
        </p>
      </Section>

      <Section n="10" title="Updates to this policy">
        <p>
          We may update this policy from time to time. Material changes will be highlighted on the Site
          or emailed to you. The "Last updated" date at the top of this page indicates when it was most
          recently revised.
        </p>
      </Section>

      <Section n="11" title="Contact us">
        <p>
          Questions about this policy? Reach us at{" "}
          <a href={`mailto:${business.supportEmail}`} className="text-primary underline">
            {business.supportEmail}
          </a>{" "}
          or by mail:
        </p>
        <p className="text-muted-foreground">{business.legalName}<br />{fullAddress()}</p>
      </Section>

      <p className="text-xs text-muted-foreground/70 mt-8 pt-6 border-t border-white/5">
        Legal entity: {business.name} is operated as a sole proprietorship by {business.soleProprietor}, {business.address.city}, {business.address.state}, {business.address.country}.
      </p>
    </LegalPage>
  );
}
